// frontend/src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  PropsWithChildren,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginApi, meApi } from '../services/auth';
import type { User } from '../types';

type AuthState = {
  user: User | null;
  token: string | null;
  /** Inicia sesión y navega a la ruta protegida. */
  login: (email: string, password: string) => Promise<void>;
  /** Cierra sesión y limpia storage. */
  logout: () => void;
  /** Permite actualizar el usuario desde pantallas como Perfil. */
  setUser: (u: User | null) => void;
  /** true cuando ya resolvió la sesión inicial. */
  ready: boolean;
  /** loading es el inverso de ready (útil para UIs). */
  loading: boolean;
};

const AuthCtx = createContext<AuthState>({
  user: null,
  token: null,
  login: async () => {},
  logout: () => {},
  setUser: () => {},
  ready: false,
  loading: true,
});

/* ============================ Helpers ============================ */

const TOKEN_KEYS = ['token', 'authToken'] as const;
const USER_KEY = 'user';

function readToken(): string | null {
  for (const k of TOKEN_KEYS) {
    const v = localStorage.getItem(k) ?? sessionStorage.getItem(k);
    if (v && v.trim()) return v;
  }
  return null;
}
function writeToken(token: string) {
  // Guardamos en localStorage por defecto (http.ts ya lo leerá)
  localStorage.setItem('token', token);
}
function clearToken() {
  for (const k of TOKEN_KEYS) {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  }
}
function normalizeRoles(input: unknown): string[] {
  const out: string[] = [];
  const add = (v: any) => {
    if (!v) return;
    if (Array.isArray(v)) return v.forEach(add);
    if (typeof v === 'string') return out.push(v);
    if (typeof v === 'object') {
      const code = v.code ?? v.name ?? v.slug ?? v.key ?? '';
      if (code) out.push(String(code));
    }
  };
  add(input);
  return out.map((r) => String(r).toUpperCase().replace(/^ROLE_/, '').trim());
}

/** Nombres calculados con fallback al local-part del email */
function buildNames(me: any) {
  const email = String(me?.email ?? '').trim();
  const emailLocal = email.includes('@') ? email.split('@')[0] : email;

  const first = String(me?.first_name ?? me?.given_name ?? '').trim();
  const last  = String(me?.last_name  ?? me?.family_name ?? '').trim();

  const apiName = typeof me?.name === 'string' ? me.name.trim() : '';
  const apiFull = typeof me?.full_name === 'string' ? me.full_name.trim() : '';

  const composed = [first, last].filter(Boolean).join(' ').trim();

  // Fallback preferido: nombres del API -> compuestos -> local-part del email
  const full_name = apiFull || apiName || composed || emailLocal;
  const name      = apiName || full_name || first || emailLocal;

  return { first_name: first, last_name: last, name, full_name };
}

/** Normaliza SIEMPRE incluyendo name y full_name */
function normalizeUser(me: any): User {
  const id         = Number(me?.id ?? me?.sub ?? 0);
  const email      = String(me?.email ?? '');
  const company_id = Number(me?.company_id ?? me?.companyId ?? 0);
  const roles      = normalizeRoles(me?.roles);
  const names      = buildNames({ ...me, email });

  return {
    id,
    email,
    company_id,
    roles,
    // En tus tipos, first_name/last_name pueden ser opcionales: añadimos si existen
    first_name: names.first_name || undefined,
    last_name: names.last_name || undefined,
    // Campos opcionales convenientes para UI
    name: names.name,
    full_name: names.full_name,
    // is_active no llega desde /me: si existe lo mapeamos, si no lo omitimos
    is_active: typeof me?.is_active === 'boolean' ? me.is_active : undefined,
  } as User;
}

/** Merge “inteligente”: conserva nombre si /me no lo trae */
function mergeUsers(base: User | null, incoming: User): User {
  if (!base) return incoming;
  return {
    ...base,
    ...incoming,
    name:       (incoming as any).name?.trim()       ? (incoming as any).name       : (base as any).name,
    full_name:  (incoming as any).full_name?.trim()  ? (incoming as any).full_name  : (base as any).full_name,
    first_name: incoming.first_name?.toString().trim() ? incoming.first_name : base.first_name,
    last_name:  incoming.last_name?.toString().trim()  ? incoming.last_name  : base.last_name,
  };
}

/* ============================ Provider ============================ */

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState<string | null>(() => readToken());
  const [user, _setUser]  = useState<User | null>(() => {
    const raw = localStorage.getItem(USER_KEY) ?? sessionStorage.getItem(USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as User; } catch { return null; }
  });
  const [ready, setReady] = useState(false);

  // Exponer setUser asegurando persistencia en storage
  const setUser = (u: User | null) => {
    _setUser(u);
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else   localStorage.removeItem(USER_KEY);
  };

  // Validación inicial contra /auth/me SIN perder el nombre guardado en login
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!token) { setReady(true); return; }
        const meResp = await meApi();
        const me         = (meResp as any)?.user ?? meResp ?? {};
        const normalized = normalizeUser(me);
        if (!alive) return;
        const merged = mergeUsers(user, normalized);
        setUser(merged);
      } catch {
        clearToken();
        localStorage.removeItem(USER_KEY);
        if (!alive) return;
        setToken(null);
        setUser(null);
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function login(email: string, password: string) {
    const resp   = await loginApi({ email, password });
    const tk     = (resp as any)?.token ?? '';
    const usrRaw = (resp as any)?.user ?? {};
    if (!tk || !tk.trim()) throw new Error('No se recibió token del servidor.');

    writeToken(tk);

    const normalized = normalizeUser({ ...usrRaw, email: usrRaw?.email ?? email });
    setToken(tk);
    setUser(normalized);

    const from = (location.state as any)?.from?.pathname as string | undefined;
    navigate(from ?? '/devioz/dashboard', { replace: true });
  }

  function logout() {
    clearToken();
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    navigate('/', { replace: true });
  }

  const value = useMemo<AuthState>(
    () => ({ user, token, login, logout, setUser, ready, loading: !ready }),
    [user, token, ready]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

/* ============================ Hook ============================ */

export function useAuth() {
  return useContext(AuthCtx);
}
